import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CTabs,
  CTabList,
  CTab,
  CTabContent,
  CTabPanel,
} from "@coreui/react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import LoadingScreen from "components/globals/LoadingScreen";

import { apiClient } from "services/Api";
import DataTableComponent from "components/DataTableComponent";
import AlertNotification from "components/globals/AlertNotification";
import TextFieldCell from "components/TextFieldCell";
import RecordDetailOffcanvas from "components/RecordDetailOffcanvas";

const InterfaceSurveillanceList = () => {
  const navigate = useNavigate();
  const [surveillances, setSurveillances] = useState([]);
  const location = useLocation();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [interfaces, setInterfaces] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [distributions, setDistributions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeInterface, setActiveInterface] = useState(
    "Interface Tecnológica",
  );
  const [previewSurveillance, setPreviewSurveillance] = useState(null);

  useEffect(() => {
    const fetchSurveillances = async (year = selectedYear) => {
      setLoading(true);
      try {
        const response = await apiClient.get(
          `/interface-surveillances/?year=${year}`,
        );
        setSurveillances(response.data.surveillances);
        setPreviewSurveillance((current) =>
          current
            ? response.data.surveillances.find(
                (surveillance) => surveillance.id === current.id,
              ) || null
            : null,
        );
        setAvailableYears(response.data.available_years);
        setPermissions(response.data.permissions);
        setInterfaces(response.data.interfaces || []);
        setKpis(response.data.kpis || null);
        setDistributions(response.data.distributions || null);
      } catch (error) {
        console.error("Error fetching surveillances:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSurveillances(selectedYear);
  }, [location.pathname, selectedYear]);

  const handleDelete = async (interfaceId) => {
    try {
      await apiClient.delete(`/interface-surveillances/${interfaceId}/`);
      setSurveillances((current) =>
        current.filter((surveillance) => surveillance.id !== interfaceId),
      );
      setPreviewSurveillance((current) =>
        current?.id === interfaceId ? null : current,
      );
    } catch (error) {
      console.error("Error deleting idea:", error);
    }
  };

  const columns = [
    {
      name: "Área",
      selector: (row) => row.area,
      sortable: true,
      className: "text-center",
    },
    {
      name: "Tópicos Relevantes",
      cell: (row) => (
        <TextFieldCell
          html={row.topics}
          onClick={() => setPreviewSurveillance(row)}
        />
      ),
      grow: 3,
    },
    {
      name: "Periodicidade",
      selector: (row) => row.periodicity,
      sortable: true,
    },
    {
      name: "Responsáveis",
      cell: (row) =>
        row.responsible_names && row.responsible_names.length > 0 ? (
          <ul
            className="mb-0 ps-3"
            style={{ cursor: "pointer" }}
            onClick={() => setPreviewSurveillance(row)}
          >
            {row.responsible_names.map((user) => (
              <li key={user}>{user}</li>
            ))}
          </ul>
        ) : (
          <span className="text-muted">Sem dados</span>
        ),
      sortable: false,
    },
  ];

  const groupedSurveillances = useMemo(() => {
    const groups = new Map();
    surveillances.forEach((item) => {
      const key = item.interface || "Sem interface";
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(item);
    });
    return groups;
  }, [surveillances]);

  const orderedInterfaces = useMemo(() => {
    const base = interfaces.length
      ? interfaces
      : Array.from(groupedSurveillances.keys());
    return base.filter(Boolean);
  }, [interfaces, groupedSurveillances]);

  useEffect(() => {
    if (!activeInterface && orderedInterfaces.length) {
      setActiveInterface(orderedInterfaces[0]);
    }
  }, [activeInterface, orderedInterfaces]);

  const buildSemesterOptions = () => {
    const items = distributions?.interfaces || [];
    return {
      chart: {
        type: "column",
        height: 260,
        spacing: [8, 8, 8, 8],
        backgroundColor: "transparent",
      },
      title: { text: null },
      credits: { enabled: false },
      exporting: { enabled: false },
      xAxis: {
        categories: items.map((item) => item.name),
        labels: { style: { fontSize: "11px" } },
      },
      yAxis: {
        min: 0,
        title: { text: null },
      },
      tooltip: {
        shared: true,
      },
      plotOptions: {
        column: {
          borderRadius: 3,
          dataLabels: {
            enabled: true,
            style: { textOutline: "none", fontSize: "10px" },
          },
        },
      },
      series: [
        {
          name: "1º semestre",
          data: items.map((item) => item.first_semester),
          color: "#7c3aed",
        },
        {
          name: "2º semestre",
          data: items.map((item) => item.second_semester),
          color: "#0f766e",
        },
        {
          name: "Planeado",
          data: items.map((item) => item.planned),
          color: "#94a3b8",
        },
      ],
    };
  };

  return (
    <>
      <AlertNotification />
      {loading ? (
        <LoadingScreen message="A carregar vigilância..." />
      ) : (
        <>
          <CRow className="g-3 mb-4">
            <CCol sm={6} lg={3}>
              <CCard className="surveillance-kpi kpi-total h-100 shadow-sm">
                <CCardBody className="py-3">
                  <div className="text-uppercase text-medium-emphasis small">
                    Ano
                  </div>
                  <div className="fs-4 fw-semibold">{selectedYear}</div>
                  <div className="text-medium-emphasis small">
                    Período selecionado
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
            <CCol sm={6} lg={3}>
              <CCard className="surveillance-kpi kpi-completion h-100 shadow-sm">
                <CCardBody className="py-3">
                  <div className="text-uppercase text-medium-emphasis small">
                    1º semestre / planeado
                  </div>
                  <div className="fs-4 fw-semibold">
                    {kpis?.first_semester_rate ?? 0}%
                  </div>
                  <div className="text-medium-emphasis small">
                    Vigilâncias semestrais
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
            <CCol sm={6} lg={3}>
              <CCard className="surveillance-kpi kpi-pending h-100 shadow-sm">
                <CCardBody className="py-3">
                  <div className="text-uppercase text-medium-emphasis small">
                    2º semestre / planeado
                  </div>
                  <div className="fs-4 fw-semibold">
                    {kpis?.second_semester_rate ?? 0}%
                  </div>
                  <div className="text-medium-emphasis small">
                    Vigilâncias semestrais
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
            <CCol sm={6} lg={3}>
              <CCard className="surveillance-kpi kpi-overdue h-100 shadow-sm">
                <CCardBody className="py-3">
                  <div className="text-uppercase text-medium-emphasis small">
                    Média semestral
                  </div>
                  <div className="fs-4 fw-semibold">
                    {kpis?.average_rate ?? 0}%
                  </div>
                  <div className="text-medium-emphasis small">
                    Vigilâncias semestrais
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>

          <CRow className="g-3 mb-4">
            <CCol>
              <CCard className="h-100 shadow-sm">
                <CCardBody className="py-3">
                  <div className="text-uppercase text-medium-emphasis small mb-2">
                    Atualizações por interface
                  </div>
                  {distributions?.interfaces?.length ? (
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={buildSemesterOptions()}
                    />
                  ) : (
                    <div className="text-medium-emphasis small">
                      Sem dados no período selecionado.
                    </div>
                  )}
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
          <CCard>
            <CCardHeader className="d-flex justify-content-between align-items-center">
              <h3 className="mb-0">Vigilância de Interfaces</h3>
              {permissions.can_create && (
                <Link
                  to={`/vigilancia-interfaces/new?interface=${activeInterface}`}
                >
                  <CButton color="info" className="text-white">
                    Nova Vigilância
                  </CButton>
                </Link>
              )}
            </CCardHeader>
            <CCardBody>
              <CTabs
                activeItemKey={activeInterface}
                onChange={setActiveInterface}
              >
                <CTabList variant="tabs" className="mb-3">
                  {orderedInterfaces.map((interfaceName) => (
                    <CTab key={interfaceName} itemKey={interfaceName}>
                      {interfaceName}
                    </CTab>
                  ))}
                </CTabList>
                <CTabContent>
                  {orderedInterfaces.map((interfaceName) => {
                    const data = groupedSurveillances.get(interfaceName) || [];
                    return (
                      <CTabPanel key={interfaceName} itemKey={interfaceName}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div />
                        </div>
                        <DataTableComponent
                          columns={columns}
                          data={data}
                          years={availableYears}
                          selectedYear={selectedYear}
                          onYearChange={(newYear) => setSelectedYear(newYear)}
                          onRowClicked={setPreviewSurveillance}
                        />
                      </CTabPanel>
                    );
                  })}
                </CTabContent>
              </CTabs>
            </CCardBody>
          </CCard>
          <RecordDetailOffcanvas
            record={previewSurveillance}
            onHide={() => setPreviewSurveillance(null)}
            title="Vigilância de Interfaces"
            subtitle={previewSurveillance?.area}
            fields={previewSurveillance ? [
              { label: "Área", value: previewSurveillance.area },
              {
                label: "Tópicos Relevantes",
                value: <TextFieldCell html={previewSurveillance.topics || "-"} />,
                plain: false,
              },
              { label: "Periodicidade", value: previewSurveillance.periodicity },
              {
                label: "Responsáveis",
                value: previewSurveillance.responsible_names?.length
                  ? previewSurveillance.responsible_names.join(", ")
                  : "-",
              },
            ] : []}
            headerActions={previewSurveillance && <>
              <CButton color="info" variant="outline" size="sm" onClick={() => navigate(`/vigilancia-interfaces/${previewSurveillance.id}`)}>
                {permissions.can_update ? "Editar" : "Ver"}
              </CButton>
              {permissions.can_delete && (
                <CButton color="danger" size="sm" className="text-white" onClick={() => {
                  if (window.confirm("Tem a certeza que quer eliminar este registo?")) handleDelete(previewSurveillance.id);
                }}>
                  Eliminar
                </CButton>
              )}
            </>}
          />
        </>
      )}
    </>
  );
};

export default InterfaceSurveillanceList;
